import type { ActionReturnType } from '../../types';
import type { ApiSessionData } from '../../../api/types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { IS_TAURI } from '../../../util/browser/globalEnvironment';
import { PASSCODE_IDB_STORE } from '../../../util/browser/idb';
import { toCredentialRequestOptions } from '../../../util/browser/passkeys';
import {
  IS_WEBAUTHN_SUPPORTED,
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { disableDebugConsole, initDebugConsole } from '../../../util/debugConsole';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { getPublicWebchatCanonicalPath, markPublicSaasWebchatContext } from '../../../util/googleAuth';
import { ACCOUNT_SLOT, getAccountsInfo } from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { clearPublicWebchatHttpFallback } from '../../../util/publicWebchatHttpFallback';
import {
  parseInitialLocationHash,
  resetInitialLocationHash,
  resetLocationHash,
} from '../../../util/routing';
import { captureSaasHandoffBootstrapFromSearch } from '../../../util/saasHandoffIntent';
import { pause } from '../../../util/schedulers';
import {
  clearCurrentSlotAuthKeysOnly,
  clearStoredSession,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { logTranslationDebugEvent } from '../../../util/translationDebug';
import { navigateToWebAuthUrl } from '../../../util/webAuthNavigation';
import { peekPendingWebAuthToken, storePendingWebAuthToken } from '../../../util/webAuthTokenBootstrap';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import { loadPendingPhoneCallStateSnapshot } from '../../phoneCallStateReloadRecovery';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { updateAuth } from '../../reducers/auth';
import { selectTranslationLanguage } from '../../selectors/settings';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';
import { buildInitApiArgs } from './initial.buildInitApiArgs';

type OwnerBootstrapResponse = {
  bootstrapMode?: string;
  loginToken?: string;
  sessionData?: {
    mainDcId?: number;
    keys?: Record<number, string> | Record<string, string>;
    dcOptions?: Record<number, {
      serverAddress?: string;
      port?: number;
      isTest?: boolean;
    }> | Record<string, {
      serverAddress?: string;
      port?: number;
      isTest?: boolean;
    }>;
    isTest?: boolean;
  };
};

type ResolvedOwnerBootstrap = {
  loginToken?: string;
  sessionData?: {
    mainDcId: number;
    keys: Record<number, string>;
    dcOptions?: Record<number, {
      serverAddress: string;
      port: number;
      isTest?: true;
    }>;
    isTest?: true;
  };
};

type ResolvedOwnerBootstrapDcOptions = NonNullable<ResolvedOwnerBootstrap['sessionData']>['dcOptions'];

const OWNER_BOOTSTRAP_CACHE_TTL_MS = 60_000;
const resolvedOwnerBootstrapByToken = new Map<string, { expiresAt: number; value: ResolvedOwnerBootstrap }>();
const pendingOwnerBootstrapByToken = new Map<string, Promise<ResolvedOwnerBootstrap>>();
const consumedOwnerBootstrapLoginTokenByToken = new Map<string, string>();
const ownerBootstrapSessionFingerprintByToken = new Map<string, string | undefined>();

function hasStoredMainAuthKey(sessionData: ApiSessionData | undefined) {
  const mainDcId = sessionData?.mainDcId;
  return Boolean(mainDcId !== undefined && sessionData?.keys?.[mainDcId]);
}

function getStoredSessionFingerprint(sessionData: ApiSessionData | undefined) {
  if (!hasStoredMainAuthKey(sessionData)) {
    return undefined;
  }

  const mainDcId = sessionData!.mainDcId!;
  const mainAuthKey = sessionData!.keys![mainDcId];
  return `${mainDcId}:${mainAuthKey}`;
}

function normalizeBootstrapSessionData(
  payload: OwnerBootstrapResponse['sessionData'],
): ResolvedOwnerBootstrap['sessionData'] | undefined {
  if (!payload || typeof payload.mainDcId !== 'number' || !payload.keys || typeof payload.keys !== 'object') {
    return undefined;
  }

  const normalizedKeys = Object.entries(payload.keys).reduce<Record<number, string>>((acc, [key, value]) => {
    const dcId = Number(key);
    if (!Number.isFinite(dcId) || typeof value !== 'string' || !value.trim()) {
      return acc;
    }
    acc[dcId] = value.trim();
    return acc;
  }, {});

  if (!Object.keys(normalizedKeys).length) {
    return undefined;
  }

  const normalizedDcOptions = payload.dcOptions && typeof payload.dcOptions === 'object'
    ? Object.entries(payload.dcOptions).reduce<Record<number, NonNullable<ResolvedOwnerBootstrapDcOptions>[number]>>(
      (acc, [key, value]) => {
        const dcId = Number(key);
        if (!Number.isFinite(dcId) || !value || typeof value !== 'object') {
          return acc;
        }
        const { serverAddress, port, isTest } = value;
        if (typeof serverAddress !== 'string' || !serverAddress.trim()) {
          return acc;
        }
        if (typeof port !== 'number' || !Number.isSafeInteger(port) || port <= 0 || port > 65535) {
          return acc;
        }
        acc[dcId] = {
          serverAddress: serverAddress.trim(),
          port,
          ...(isTest ? { isTest: true as const } : {}),
        };
        return acc;
      },
      {},
    )
    : undefined;

  return {
    mainDcId: payload.mainDcId,
    keys: normalizedKeys,
    ...(normalizedDcOptions && Object.keys(normalizedDcOptions).length ? { dcOptions: normalizedDcOptions } : {}),
    ...(payload.isTest ? { isTest: true as const } : {}),
  };
}

async function resolveFreshSaasOwnerBootstrap(ownerAccessToken: string): Promise<ResolvedOwnerBootstrap> {
  try {
    const response = await window.fetch('/api/run/webchat-owner-bootstrap', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        ownerAccessToken,
        sourceSurface: 'run_saas_webchat_compat',
      }),
    });
    if (!response.ok) {
      return {};
    }

    const payload = await response.json() as OwnerBootstrapResponse;
    if (payload.bootstrapMode === 'login_token' && payload.loginToken?.trim()) {
      return {
        loginToken: payload.loginToken.trim(),
      };
    }

    if (payload.bootstrapMode === 'session_data') {
      const sessionData = normalizeBootstrapSessionData(payload.sessionData);
      if (sessionData) {
        return { sessionData };
      }
    }

    return {};
  } catch {
    return {};
  }
}

async function fetchSaasOwnerBootstrap(ownerAccessToken: string): Promise<ResolvedOwnerBootstrap> {
  const now = Date.now();
  const cached = resolvedOwnerBootstrapByToken.get(ownerAccessToken);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  const pending = pendingOwnerBootstrapByToken.get(ownerAccessToken);
  if (pending) {
    return pending;
  }

  const request = resolveFreshSaasOwnerBootstrap(ownerAccessToken)
    .then((value) => {
      resolvedOwnerBootstrapByToken.set(ownerAccessToken, {
        expiresAt: Date.now() + OWNER_BOOTSTRAP_CACHE_TTL_MS,
        value,
      });
      return value;
    })
    .finally(() => {
      pendingOwnerBootstrapByToken.delete(ownerAccessToken);
    });

  pendingOwnerBootstrapByToken.set(ownerAccessToken, request);
  return request;
}

function isSaasWorkReturnPath(path?: string): boolean {
  if (!path) return false;
  try {
    if (!path.startsWith('/')) return false;
    if (path.startsWith('//') || path.startsWith('/\\')) return false;
    const pathname = new URL(path, 'https://run.ceo').pathname;
    return pathname === '/work' || pathname.startsWith('/work/');
  } catch {
    return false;
  }
}

function resolveEffectiveSaasWorkTargetHost(
  targetHost: string | undefined,
  returnTo: string | undefined,
): string | undefined {
  if (targetHost) {
    return targetHost;
  }
  if (window.location.host === 'run.i.run.ceo' && returnTo && isSaasWorkReturnPath(returnTo)) {
    return window.location.host;
  }
  return undefined;
}

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const saasBootstrapHint = captureSaasHandoffBootstrapFromSearch();
  const initialLocationHash = parseInitialLocationHash();
  const pendingWebAuthToken = initialLocationHash?.tgWebAuthToken ? undefined : peekPendingWebAuthToken();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    shouldCollectTranslationDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const isTestServer = global.config?.isTestServer;
  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo)
    .filter((info) => info.isTest === isTestServer)
    .map(({ userId }) => userId)
    .filter(Boolean);

  const shouldIgnoreStoredSessionForSaasHandoff =
    saasBootstrapHint?.source === 'saas'
    && Boolean(saasBootstrapHint.ownerAccessToken);
  const shouldBypassOwnerBootstrapForWorkReturn =
    shouldIgnoreStoredSessionForSaasHandoff
    && isSaasWorkReturnPath(saasBootstrapHint?.returnTo)
    && resolveEffectiveSaasWorkTargetHost(saasBootstrapHint?.targetHost, saasBootstrapHint?.returnTo) === window.location.host;

  if (shouldIgnoreStoredSessionForSaasHandoff) {
    clearPublicWebchatHttpFallback();
  }

  let activeOwnerAccessToken: string | undefined;
  let leasedLoginToken: string | undefined;
  void (async () => {
    const ownerAccessToken = saasBootstrapHint?.ownerAccessToken;
    activeOwnerAccessToken = ownerAccessToken;
    const bootstrap = shouldIgnoreStoredSessionForSaasHandoff
      && !shouldBypassOwnerBootstrapForWorkReturn
      && !pendingWebAuthToken
      && ownerAccessToken
      ? await fetchSaasOwnerBootstrap(ownerAccessToken)
      : {};
    let loginToken = bootstrap.loginToken;
    let sessionData = shouldIgnoreStoredSessionForSaasHandoff && !shouldBypassOwnerBootstrapForWorkReturn
      ? bootstrap.sessionData
      : loadStoredSession();
    leasedLoginToken = undefined;

    if (
      shouldIgnoreStoredSessionForSaasHandoff
      && !shouldBypassOwnerBootstrapForWorkReturn
      && ownerAccessToken
      && loginToken
    ) {
      const consumedLoginToken = consumedOwnerBootstrapLoginTokenByToken.get(ownerAccessToken);
      if (consumedLoginToken === loginToken) {
        loginToken = undefined;
        const storedSession = loadStoredSession();
        const currentSessionFingerprint = getStoredSessionFingerprint(storedSession);
        const leasedSessionFingerprint = ownerBootstrapSessionFingerprintByToken.get(ownerAccessToken);
        if (
          currentSessionFingerprint
          && currentSessionFingerprint !== leasedSessionFingerprint
        ) {
          sessionData = storedSession;
        } else {
          return;
        }
      } else {
        consumedOwnerBootstrapLoginTokenByToken.set(ownerAccessToken, loginToken);
        ownerBootstrapSessionFingerprintByToken.set(
          ownerAccessToken,
          getStoredSessionFingerprint(loadStoredSession()),
        );
        leasedLoginToken = loginToken;
      }
    }

    if (
      shouldIgnoreStoredSessionForSaasHandoff
      && !shouldBypassOwnerBootstrapForWorkReturn
      && ownerAccessToken
      && !loginToken
      && !sessionData
    ) {
      actions.apiUpdate({
        '@type': 'updateWebAuthTokenFailed',
        errorCode: 'OWNER_BOOTSTRAP_UNAVAILABLE',
      });
      return;
    }

    await initApi(actions.apiUpdate, buildInitApiArgs({
      initialLocationHash,
      pendingWebAuthToken,
      loginToken,
      sharedSettings: {
        shouldAllowHttpTransport,
        shouldForceHttpTransport,
        shouldDebugExportedSenders,
        language,
      },
      userAgent: navigator.userAgent,
      platform: PLATFORM_ENV,
      sessionData,
      isWebmSupported: IS_WEBM_SUPPORTED,
      maxBufferSize: MAX_BUFFER_SIZE,
      isTestServerRequested: hasTestParam,
      accountIds,
      hasPasskeySupport: IS_WEBAUTHN_SUPPORTED,
    }));
  })().catch((err) => {
    if (
      shouldIgnoreStoredSessionForSaasHandoff
      && activeOwnerAccessToken
      && leasedLoginToken
      && consumedOwnerBootstrapLoginTokenByToken.get(activeOwnerAccessToken) === leasedLoginToken
    ) {
      consumedOwnerBootstrapLoginTokenByToken.delete(activeOwnerAccessToken);
      ownerBootstrapSessionFingerprintByToken.delete(activeOwnerAccessToken);
    }
    if (shouldIgnoreStoredSessionForSaasHandoff && saasBootstrapHint?.ownerAccessToken) {
      actions.apiUpdate({
        '@type': 'updateWebAuthTokenFailed',
        errorCode: err instanceof Error ? err.message : String(err),
      });
    }
  });
  void Promise.resolve(callApi('restorePhoneCallStateSnapshot', loadPendingPhoneCallStateSnapshot())).catch(() => {});

  const shouldEnableDebugLog = Boolean(shouldCollectDebugLogs || shouldCollectTranslationDebugLogs);
  void Promise.resolve(setShouldEnableDebugLog(shouldEnableDebugLog)).catch(() => {});
  if (shouldEnableDebugLog) {
    initDebugConsole();
  } else {
    disableDebugConsole();
  }
  logTranslationDebugEvent(Boolean(shouldCollectTranslationDebugLogs), 'translation_debug_bootstrap', {
    browserLocale: navigator.language,
    browserLocales: navigator.languages,
    htmlLang: document.documentElement.lang,
    appLanguage: language,
    translationLanguage: global.settings.byKey.translationLanguage,
    targetLanguage: selectTranslationLanguage(global),
    userAgent: navigator.userAgent,
  });
});

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('requestAuthSms', (global): ActionReturnType => {
  void callApi('provideAuthResendCode');
  return updateAuth(global, {
    isLoading: true,
    canRequestSms: false,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload;

  void callApi('provideAuthCode', code);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload;

  void callApi('provideAuthPassword', password);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('loginWithPasskey', async (global, actions, payload): Promise<void> => {
  const passkeyOption = global.auth.passkeyOption;
  if (!passkeyOption) return;

  const credential = await navigator.credentials.get(toCredentialRequestOptions(passkeyOption)).catch((e: unknown) => {
    actions.showNotification({
      message: {
        key: 'PasskeyLoginError',
      },
      tabId: getCurrentTabId(),
    });
  });
  if (!credential) return;

  const publicKeyCredential = credential as PublicKeyCredential;
  callApi('restartAuthWithPasskey', publicKeyCredential.toJSON());
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  actions.loadFullUser({ userId: global.currentUserId! });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload;

  void callApi('provideAuthRegistration', { firstName, lastName });

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  clearCurrentSlotAuthKeysOnly();
  void callApi('restartAuth');

  return updateAuth(global, {
    state: 'authorizationStateWaitPhoneNumber',
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('signInWithGoogle', (global, actions, payload): ActionReturnType => {
  const { idToken } = payload;
  // eslint-disable-next-line no-console
  console.info('[run-google-auth] sign_in_start', {
    credentialLength: idToken?.length ?? 0,
  });

  global = getGlobal();
  global = updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
  setGlobal(global);

  try {
    const nextHash = `#?tgWebAuthToken=${encodeURIComponent(idToken)}`;
    clearStoredSession(ACCOUNT_SLOT);
    storePendingWebAuthToken(idToken);
    markPublicSaasWebchatContext(window.location.search, window.location.pathname);
    const nextPathname = getPublicWebchatCanonicalPath(window.location.pathname);
    const nextUrl = `${nextPathname}${window.location.search}${nextHash}`;
    // eslint-disable-next-line no-console
    console.info('[run-google-auth] sign_in_reload_with_web_token', { nextUrl });
    navigateToWebAuthUrl(nextUrl);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-google-auth] sign_in_error', {
      message: err instanceof Error
        ? err.message
        : ((err && typeof err === 'object' && 'message' in err) ? String((err as any).message) : String(err)),
    });
    global = getGlobal();
    global = updateAuth(global, {
      isLoading: false,
      errorKey: 'Google sign-in failed' as any,
    });
    setGlobal(global);
  }
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return updateAuth(global, {
    isLoadingQrCode: true,
    errorKey: undefined,
    hasWebAuthTokenFailed: false,
  });
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData, global.currentUserId);
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();

  const shouldNavigateAway = Boolean(
    payload?.returnUrl && !IS_TAURI && typeof window !== 'undefined',
  );

  // Skip initApi when we are about to replace the page — the browser aborts
  // the mtproto handshake on navigation and all the post-reset bootstrap
  // work would be wasted.
  if (payload?.forceInitApi && !shouldNavigateAway) {
    actions.initApi();
  }

  if (shouldNavigateAway && payload?.returnUrl) {
    window.location.assign(payload.returnUrl);
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);
  clearEncryptedSession();

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  // Check if there are any accounts left
  const accounts = getAccountsInfo();
  if (!Object.values(accounts).length) {
    PASSCODE_IDB_STORE.clear();
    removeSharedStateFromCache();
  }

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = updateAuth(global, {
    nearestCountry: authNearestCountry,
  });
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, payload): ActionReturnType => {
  const { token } = payload;
  return {
    ...global,
    push: {
      deviceToken: token,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...loadStoredSession(), userId: global.currentUserId });
  const globalJson = serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
